<!--
  ConvertTextToHtmlModal.vue

  Mostra o ensaio (dry-run) da conversão de um texto para HTML e, só então, pede
  confirmação. Operação administrativa: o backend exige o papel Sys nos dois
  endpoints — esconder o botão é conveniência de interface, não autorização.

  O ensaio existe porque a conversão é irreversível fora do backup: ela reescreve o
  arquivo do texto e os offsets de todas as notas dele.
-->
<script setup>
import { ref, computed, watch } from "vue";
import {
  dryRunConversion,
  convertTextToHtml,
} from "@/services/textConversionService";

const props = defineProps({
  show: { type: Boolean, default: false },
  textId: { type: Number, default: null },
});

const emit = defineEmits(["close", "converted"]);

const loading = ref(false);
const converting = ref(false);
const error = ref("");
const preview = ref(null);
const result = ref(null);

// Vereditos que impedem a conversão. Os outros — Unchanged, Shifted, NotAnchored —
// são resultados normais.
const BLOCKING = new Set(["TextChanged", "Lost", "OutOfRange"]);

const outcomeCounts = computed(() => {
  const counts = {};
  for (const note of preview.value?.notes ?? []) {
    counts[note.outcome] = (counts[note.outcome] ?? 0) + 1;
  }
  return counts;
});

const blockingNotes = computed(() =>
  (preview.value?.notes ?? []).filter((n) => BLOCKING.has(n.outcome))
);

const canConvert = computed(
  () => !!preview.value?.safeToConvert && !converting.value && !result.value
);

const deltaLabel = computed(() => {
  const delta = preview.value?.quotaDelta ?? 0;
  return delta > 0 ? `+${delta}` : String(delta);
});

watch(
  () => [props.show, props.textId],
  async ([show, textId]) => {
    if (!show || !textId) return;
    await loadPreview(textId);
  },
  { immediate: true }
);

async function loadPreview(textId) {
  loading.value = true;
  error.value = "";
  preview.value = null;
  result.value = null;

  try {
    const response = await dryRunConversion(textId);
    preview.value = response.data;
  } catch (e) {
    error.value =
      e.response?.data?.message ?? e.message ?? "Falha ao simular a conversão.";
  } finally {
    loading.value = false;
  }
}

async function confirmConversion() {
  if (!canConvert.value) return;

  converting.value = true;
  error.value = "";

  try {
    const response = await convertTextToHtml(props.textId);
    result.value = response.data;
    emit("converted", result.value);
  } catch (e) {
    error.value =
      e.response?.data?.message ?? e.message ?? "Falha ao converter o texto.";
  } finally {
    converting.value = false;
  }
}

function close() {
  emit("close");
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
      @click.self="close"
    >
      <div
        class="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-lg bg-surface p-5 shadow-xl "
        @click.stop
      >
        <h2 class="mb-1 text-lg font-bold">Converter conteúdo para HTML</h2>
        <p class="mb-4 text-sm opacity-75">
          Nada foi gravado ainda. Confira o relatório abaixo antes de confirmar.
        </p>

        <div v-if="loading" class="py-6 text-center text-sm opacity-75">
          Simulando a conversão…
        </div>

        <div
          v-else-if="error"
          class="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 "
        >
          {{ error }}
        </div>

        <!-- ── Resultado da conversão já executada ─────────────────────── -->
        <div
          v-if="result"
          class="rounded border border-green-300 bg-green-50 p-3 text-sm "
        >
          <p class="font-semibold">Texto convertido.</p>
          <p class="mt-1">
            {{ result.notesRemapped }} nota(s) remapeada(s).
            Tamanho {{ result.oldTextLength }} → {{ result.newTextLength }}.
          </p>
          <p class="mt-2 break-all opacity-80">
            Backup do conteúdo original:
            <code>{{ result.backupPath }}</code>
          </p>
          <p class="mt-2 opacity-80">
            É o único jeito de desfazer — os campos do banco teriam de ser
            revertidos à mão.
          </p>
        </div>

        <!-- ── Relatório do ensaio ─────────────────────────────────────── -->
        <template v-else-if="preview">
          <dl class="mb-4 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
            <div>
              <dt class="opacity-60">Conteúdo</dt>
              <dd>{{ preview.oldLength }} → {{ preview.newLength }}</dd>
            </div>
            <div>
              <dt class="opacity-60">Delta de cota</dt>
              <dd>{{ deltaLabel }}</dd>
            </div>
            <div>
              <dt class="opacity-60">Blocos</dt>
              <dd>{{ preview.blockCount }}</dd>
            </div>
            <div>
              <dt class="opacity-60">Notas</dt>
              <dd>{{ preview.notes.length }}</dd>
            </div>
            <div>
              <dt class="opacity-60">Maior offset de nota</dt>
              <dd>
                {{ preview.oldMaxNoteRangeEnd ?? "—" }} →
                {{ preview.newMaxNoteRangeEnd ?? "—" }}
              </dd>
            </div>
            <div>
              <dt class="opacity-60">Recuo descartado</dt>
              <dd>{{ preview.blocksWithDiscardedIndent }} bloco(s)</dd>
            </div>
          </dl>

          <div class="mb-4 flex flex-wrap gap-2 text-xs">
            <span
              v-for="(count, outcome) in outcomeCounts"
              :key="outcome"
              class="rounded px-2 py-1"
              :class="
 BLOCKING.has(outcome)
                  ? 'bg-red-100 text-red-800  '
                  : 'bg-gray-100 '
              "
            >
              {{ outcome }}: {{ count }}
            </span>
          </div>

          <ul
            v-if="preview.warnings.length"
            class="mb-4 list-disc space-y-1 rounded border border-amber-300 bg-amber-50 p-3 pl-6 text-sm text-amber-900 "
          >
            <li v-for="(warning, i) in preview.warnings" :key="i">
              {{ warning }}
            </li>
          </ul>

          <!-- Notas que impedem a conversão: mostradas uma a uma, porque cada
               uma precisa de decisão humana. -->
          <div v-if="blockingNotes.length" class="mb-4">
            <h3 class="mb-1 text-sm font-semibold">
              Notas que impedem a conversão
            </h3>
            <ul class="space-y-1 text-xs">
              <li
                v-for="note in blockingNotes"
                :key="note.noteId"
                class="rounded bg-red-50 p-2 "
              >
                <span class="font-semibold">#{{ note.noteId }}</span>
                {{ note.noteTitle }} — {{ note.outcome }}
                ({{ note.oldStart }}–{{ note.oldEnd }})
              </li>
            </ul>
          </div>

          <details class="mb-4 text-xs">
            <summary class="cursor-pointer opacity-75">
              Ver o começo do texto convertido
            </summary>
            <pre
              class="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-gray-100 p-2 "
            >{{ preview.projectionSample }}</pre>
          </details>

          <p
            v-if="!preview.safeToConvert"
            class="mb-3 text-sm font-semibold text-red-700 "
          >
            O ensaio reprovou esta conversão. Resolva os pontos acima antes.
          </p>
        </template>

        <div class="mt-4 flex justify-end gap-2">
          <button
            type="button"
            class="rounded px-4 py-2 text-sm hover:bg-gray-100 "
            @click="close"
          >
            {{ result ? "Fechar" : "Cancelar" }}
          </button>
          <button
            v-if="!result"
            type="button"
            class="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="!canConvert"
            @click="confirmConversion"
          >
            {{ converting ? "Convertendo…" : "Converter" }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
